import type { GlyphName } from './glyphs';

/**
 * Ícono de Ruttia para cada menú, por su ruta del estándar (la base guarda el nombre de un ícono de
 * Material en `menu.icon`; no se cambia). Si una ruta no está aquí, se usa ese ícono de Material.
 */
export const ROUTE_GLYPHS: Record<string, GlyphName> = {
  '/admin/users': 'users',
  '/admin/offices': 'office',
  '/admin/sales-reps': 'sales-rep',
  '/admin/clients': 'clients',
  '/admin/routes': 'route',
  '/admin/route-defaults': 'settings',
  '/admin/storage': 'delete',
  '/admin/loan-types': 'loan',
  '/admin/expense-types': 'expense',
  '/admin/banks': 'bank',
  '/admin/permissions': 'lock',
  '/admin/devices': 'device',
  '/admin/alerts': 'alert',
  '/loans/payments': 'payment',
  '/loans/cash-box-expenses/new': 'cash-box',
  '/loans/expenses/new': 'expense',
  '/loans/expenses': 'expense',
  '/loans/route-devices': 'device',
  '/loans/payment-corrections': 'edit',
  '/loans/correction-approvals': 'paid',
  '/processes/visit-order': 'reorder',
  '/processes/route-tracking': 'gps',
  '/processes/route-settlement': 'end-day',
  '/processes/export': 'export',
  '/processes/import': 'import',
  '/processes/imports': 'history',
  '/processes/loan-transfers': 'transfer',
  '/processes/sync-audit': 'sync',
  '/processes/app-notices': 'notice',
  '/reports/payments-by-date': 'calendar',
  '/reports/cancellations': 'paid',
  '/reports/expenses': 'expense',
  '/reports/client-history': 'history',
  '/reports/loans-by-date': 'loan',
  '/reports/balances': 'summary',
  '/reports/cash-differences': 'cash-box',
  '/reports/daily-summary': 'summary',
  '/reports/discounts': 'receipt',
  '/reports/app-sync': 'sync',
  '/reports/deleted-records': 'delete',
  '/reports/sales-rep-risk': 'alert',
  '/reports/late-loans': 'calendar',
  '/reports/consolidated-day': 'report',
  '/reports/cash-boxes': 'cash-box',
  '/reports/cash-movements': 'receipt',
  '/reports/refinancing': 'refinance',
  '/inventory/products': 'inventory',
  '/inventory/receipts': 'stock-in',
  '/inventory/transfers': 'transfer',
  '/inventory/write-offs': 'stock-out',
  '/inventory/returns': 'return',
  '/inventory/stock': 'report',
};

/** Módulos, por el ícono de Material que guarda la base (`module.icon`). */
export const MODULE_GLYPHS: Record<string, GlyphName> = {
  ManageAccountsTwoToneIcon: 'settings',
  CardTravelTwoToneIcon: 'loan',
  ArticleTwoToneIcon: 'report',
  AssessmentTwoToneIcon: 'report',
  WorkTwoToneIcon: 'route',
  Inventory2TwoToneIcon: 'inventory',
};

const normalize = (route?: string | null) => {
  const r = String(route || '').trim().toLowerCase().replace(/\/+$/, '');
  return r.startsWith('/') ? r : `/${r}`;
};

export const menuGlyph = (route?: string | null): GlyphName | null => ROUTE_GLYPHS[normalize(route)] ?? null;

/** Módulos por su nombre (en español o el del estándar), cuando la base no trae un ícono conocido. */
const MODULE_NAME_GLYPHS: Record<string, GlyphName> = {
  administrar: 'settings',
  admin: 'settings',
  prestamos: 'loan',
  loans: 'loan',
  informes: 'report',
  reports: 'report',
  reportes: 'summary',
  consolidated: 'summary',
  procesos: 'route',
  processes: 'route',
  inventario: 'inventory',
  inventory: 'inventory',
};

const cleanName = (value?: string | null) =>
  String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .trim()
    .toLowerCase();

/**
 * Ícono de un módulo: el que corresponde al ícono de Material guardado en la base y, si no se conoce,
 * el de su nombre. Si tampoco se conoce el nombre, se usa el de la primera opción del módulo con ícono.
 */
export const moduleGlyph = (icon?: string | null, name?: string | null, routes: (string | null | undefined)[] = []): GlyphName => {
  const byIcon = icon ? MODULE_GLYPHS[icon] : undefined;
  if (byIcon) return byIcon;
  const byName = MODULE_NAME_GLYPHS[cleanName(name)];
  if (byName) return byName;
  for (const route of routes) {
    const glyph = menuGlyph(route);
    if (glyph) return glyph;
  }
  return 'report';
};
